import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { Add, Delete, Edit, PrecisionManufacturing } from '@mui/icons-material';
import api from '../../services/api';
import type { Machine, MaterialIssue, MixBatch, PlanLine, PlanResult, ProductionKpis, SaveMachine, SaveShiftEntry, Shift, ShiftEntry } from '../../types/factory';
import { DUE_CHIP, EQUIPMENT_STATUS, Kpi, KpiStrip, MaterialSelect, PageHeader, ProductSelect, StatusChip, addDaysIso, errorMessage, fmtPct, fmtQty, isoDay, loadMaterials, num, todayIso, useMaterials, useProducts, usePageView } from './shared';
import type { PageView } from './shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDay } from '../../components/commercial/contact';
import { TraceDialog } from './FactoryMaterialsPage';

const SHIFTS: { value: Shift; label: string }[] = [
  { value: 'TM', label: 'Mañana' },
  { value: 'TT', label: 'Tarde' },
  { value: 'TN', label: 'Noche' },
];

const effColor = (e?: number | null): 'success' | 'warning' | 'error' | 'default' =>
  e == null ? 'default' : e >= 0.9 ? 'success' : e >= 0.7 ? 'warning' : 'error';

const isMold = (m: Machine) => m.kind === 'Matriz';

const VIEWS: PageView[] = [
  { key: 'parte', label: 'Parte diario', description: 'Lo que sale de cada máquina por turno: bueno, rotos y horas. La eficiencia se compara contra el estándar de la matriz.' },
  { key: 'planificar', label: 'Qué fabricar', description: 'Cargá qué querés producir y te dice qué insumos faltan, cuántas horas de máquina lleva y qué paradas de mantenimiento hay en el medio.' },
  { key: 'salidas', label: 'Salidas de MP a máquina', description: 'Materia prima que sale del depósito hacia cada inyectora.' },
  { key: 'indicadores', label: 'Indicadores de producción', description: 'Producción contra estándar por máquina, por producto y por día.' },
  { key: 'maquinas', label: 'Máquinas y matrices', description: 'Inyectoras, sopladoras y matrices: bocas, ciclo, piezas por hora y dónde está montada cada matriz.' },
];

/** Planificación, parte de producción por máquina y turno, salidas de MP, indicadores y equipos. */
const FactoryProductionPage: React.FC = () => {
  const location = useLocation();
  // El tablero de stock manda a planificar con ?tab=plan y las líneas en el state.
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS, new URLSearchParams(location.search).get('tab') === 'plan' ? 1 : 0);
  const [machines, setMachines] = useState<Machine[]>([]);
  const loadMachines = useCallback(async () => setMachines(await api.getMachines(true)), []);
  useEffect(() => { loadMachines().catch(() => undefined); }, [loadMachines]);

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Producción'}
        description={flat && current.description ? current.description : <>Planificá qué fabricar con lo que hay, cargá lo que sale de cada máquina por turno (suma al stock) y las salidas de materia prima.</>}
      />
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Parte diario" />
        <Tab label="Planificar" />
        <Tab label="Salidas de MP" />
        <Tab label="Indicadores" />
        <Tab label="Máquinas y matrices" />
      </Tabs>)}
      {tab === 0 && <ShiftsTab machines={machines.filter(m => m.isActive && !isMold(m))} molds={machines.filter(m => m.isActive && isMold(m))} />}
      {tab === 1 && <PlanTab machines={machines.filter(m => m.isActive)} />}
      {tab === 2 && <IssuesTab machines={machines.filter(m => m.isActive && !isMold(m))} />}
      {tab === 3 && <KpisTab />}
      {tab === 4 && <MachinesTab machines={machines} reload={loadMachines} />}
    </Box>
  );
};

// ==================== Parte diario ====================

type ShiftForm = Omit<SaveShiftEntry, 'goodQuantity' | 'scrapQuantity' | 'hours'> & {
  id?: number;
  good: string;
  scrap: string;
  hours: string;
  inPackages: boolean;
};

const emptyShift = (date: string, shift: Shift = 'TM'): ShiftForm => ({
  date, shift, machineId: null, moldId: null, mixBatchId: null, productId: 0, good: '', scrap: '', hours: '12', operatorName: '', notes: '',
  applyStock: true, consumeMaterials: false, inPackages: false,
});

const ShiftsTab: React.FC<{ machines: Machine[]; molds: Machine[] }> = ({ machines, molds }) => {
  const [from, setFrom] = useState(addDaysIso(todayIso(), -6));
  const [to, setTo] = useState(todayIso());
  const [machineId, setMachineId] = useState<number | ''>('');
  const [rows, setRows] = useState<ShiftEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ShiftForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [mixes, setMixes] = useState<MixBatch[]>([]);
  const [traceMixId, setTraceMixId] = useState<number | null>(null);
  // Lotes de mezcla de las últimas dos semanas para elegir en el parte.
  useEffect(() => { api.getMixBatches({ from: addDaysIso(todayIso(), -14), to: todayIso() }).then(setMixes).catch(() => setMixes([])); }, []);
  // Productos que se lotean a la salida de máquina: su stock lo suma la pesada, no el parte (si no, cuenta dos veces).
  const [lotted, setLotted] = useState<Set<number>>(new Set());
  useEffect(() => { api.getLotConfigs().then(cs => setLotted(new Set(cs.filter(c => c.isActive).map(c => c.productId)))).catch(() => undefined); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.getShiftEntries({ from, to, machineId: machineId || undefined }));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'No se pudo cargar el parte'));
    } finally {
      setLoading(false);
    }
  }, [from, to, machineId]);
  useEffect(() => { load(); }, [load]);

  const machine = machines.find(m => m.id === form?.machineId);
  const mold = molds.find(m => m.id === form?.moldId);
  const standard = mold?.effectiveUnitsPerHour ?? machine?.effectiveUnitsPerHour;
  const perPackage = machine?.unitsPerPackage || 0;
  const packageName = machine?.packageName || 'bolsa';

  const save = async (another: boolean) => {
    if (!form) return;
    if (!form.productId) { setError('Elegí el producto'); return; }
    const factor = form.inPackages && perPackage ? perPackage : 1;
    const payload: SaveShiftEntry = {
      date: form.date,
      shift: form.shift,
      machineId: form.machineId || null,
      moldId: form.moldId || null,
      mixBatchId: form.mixBatchId || null,
      productId: form.productId,
      goodQuantity: num(form.good) * factor,
      scrapQuantity: num(form.scrap) * factor,
      hours: form.hours === '' ? null : num(form.hours),
      operatorName: form.operatorName || null,
      notes: form.notes || null,
      applyStock: form.applyStock,
      consumeMaterials: form.applyStock && form.consumeMaterials,
    };
    setSaving(true);
    try {
      if (form.id) await api.updateShiftEntry(form.id, payload);
      else await api.createShiftEntry(payload);
      setError(null);
      // "Guardar y otro": mismo día y turno, la máquina siguiente.
      if (another && !form.id) {
        const idx = machines.findIndex(m => m.id === form.machineId);
        const next = machines[idx + 1];
        setForm({ ...emptyShift(form.date, form.shift), hours: form.hours, machineId: next?.id ?? null, inPackages: form.inPackages, consumeMaterials: form.consumeMaterials });
      } else {
        setForm(null);
      }
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: ShiftEntry) => {
    if (!window.confirm(`¿Borrar el renglón de ${r.productName} (${r.shift} ${formatDay(r.date)})? Si movió stock, se revierte.`)) return;
    try {
      await api.deleteShiftEntry(r.id);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo borrar'));
    }
  };

  const edit = (r: ShiftEntry) => setForm({
    id: r.id, date: isoDay(r.date), shift: r.shift, machineId: r.machineId ?? null, moldId: r.moldId ?? null, mixBatchId: r.mixBatchId ?? null, productId: r.productId,
    good: String(r.goodQuantity), scrap: String(r.scrapQuantity), hours: r.hours == null ? '' : String(r.hours),
    operatorName: r.operatorName || '', notes: r.notes || '', applyStock: r.stockApplied, consumeMaterials: false, inPackages: false,
  });

  const totals = useMemo(() => ({
    good: rows.reduce((a, r) => a + r.goodQuantity, 0),
    scrap: rows.reduce((a, r) => a + r.scrapQuantity, 0),
  }), [rows]);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" select label="Máquina" value={machineId} onChange={e => setMachineId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 180 }}>
          <MenuItem value="">Todas</MenuItem>
          {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm(emptyShift(todayIso()))}>Cargar turno</Button>
      </Stack>
      {machines.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>Cargá las máquinas en la pestaña "Máquinas" para medir la eficiencia contra el estándar.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Turno</TableCell>
                  <TableCell>Máquina</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Bueno</TableCell>
                  <TableCell align="right">Rotos</TableCell>
                  <TableCell align="right">Horas</TableCell>
                  <TableCell align="right">Estándar</TableCell>
                  <TableCell align="right">Eficiencia</TableCell>
                  <TableCell>Operario</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay partes cargados en estas fechas.</TableCell></TableRow>
                )}
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{formatDay(r.date)}</TableCell>
                    <TableCell>{r.shift}</TableCell>
                    <TableCell>{r.machineName || '—'}{r.moldName && <Typography variant="caption" display="block" color="text.secondary">{r.moldName}</Typography>}</TableCell>
                    <TableCell>
                      {r.productName}
                      {!r.stockApplied && <Chip size="small" label="sin stock" sx={{ ml: 1 }} />}
                      {r.mixBatchCode && (
                        <Box component="button" type="button" onClick={() => setTraceMixId(r.mixBatchId!)}
                          sx={{ all: 'unset', display: 'block', cursor: 'pointer', fontSize: 12, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                          Lote {r.mixBatchCode}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtQty(r.goodQuantity)}</TableCell>
                    <TableCell align="right" sx={{ color: r.scrapQuantity > 0 ? 'error.main' : undefined }}>{fmtQty(r.scrapQuantity)}</TableCell>
                    <TableCell align="right">{r.hours ?? '—'}</TableCell>
                    <TableCell align="right">{fmtQty(r.expectedQuantity)}</TableCell>
                    <TableCell align="right">
                      {r.efficiencyPct == null ? '—' : <Chip size="small" color={effColor(r.efficiencyPct)} label={fmtPct(r.efficiencyPct)} />}
                    </TableCell>
                    <TableCell>{r.operatorName || ''}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => edit(r)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4}><b>Total</b></TableCell>
                    <TableCell align="right"><b>{fmtQty(totals.good)}</b></TableCell>
                    <TableCell align="right"><b>{fmtQty(totals.scrap)}</b></TableCell>
                    <TableCell colSpan={5} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {traceMixId && <TraceDialog kind="mix" id={traceMixId} onClose={() => setTraceMixId(null)} />}
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Editar renglón del parte' : 'Cargar turno'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField size="small" type="date" label="Fecha" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} InputLabelProps={{ shrink: true }} />
                <ToggleButtonGroup exclusive size="small" value={form.shift} onChange={(_, v) => v && setForm({ ...form, shift: v })}>
                  {SHIFTS.map(s => <ToggleButton key={s.value} value={s.value}>{s.value} · {s.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Stack>
              <TextField size="small" select label="Máquina" value={form.machineId ?? ''} onChange={e => setForm({ ...form, machineId: e.target.value === '' ? null : Number(e.target.value), inPackages: false })}>
                <MenuItem value="">Sin máquina (armado, pegado…)</MenuItem>
                {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
              {molds.length > 0 && (
                <TextField size="small" select label="Matriz" value={form.moldId ?? ''} onChange={e => setForm({ ...form, moldId: e.target.value === '' ? null : Number(e.target.value) })}>
                  <MenuItem value="">—</MenuItem>
                  {molds.map(m => <MenuItem key={m.id} value={m.id}>{m.name}{m.status !== 'Disponible' ? ` (${EQUIPMENT_STATUS[m.status]?.label})` : ''}</MenuItem>)}
                </TextField>
              )}
              <ProductSelect value={form.productId || null} onChange={id => setForm({ ...form, productId: id ?? 0, applyStock: form.id ? form.applyStock : !(id && lotted.has(id)) })} required />
              {form.productId > 0 && lotted.has(form.productId) && (
                <Alert severity="info" icon={false}>Este producto se lotea: el stock lo suman las pesadas en Lotes y pallets. El parte queda para horas, rotos y eficiencia.</Alert>
              )}
              {mixes.length > 0 && (
                <TextField size="small" select label="Lote de mezcla" value={form.mixBatchId ?? ''}
                  helperText="La tanda que se cargó en la inyectora: deja rastreable el parte hasta la recepción de MP."
                  onChange={e => {
                    const mix = mixes.find(m => m.id === Number(e.target.value));
                    setForm({ ...form, mixBatchId: mix?.id ?? null, productId: form.productId || mix?.productId || 0, machineId: form.machineId ?? mix?.machineId ?? null });
                  }}>
                  <MenuItem value="">Sin lote</MenuItem>
                  {mixes.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.code} · {fmtQty(m.totalKg)} kg{m.mixRecipeName ? ` · ${m.mixRecipeName}` : ''}{m.machineName ? ` · ${m.machineName}` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {perPackage > 0 && (
                <FormControlLabel
                  control={<Checkbox checked={form.inPackages} onChange={e => setForm({ ...form, inPackages: e.target.checked })} />}
                  label={`Cargar en ${packageName}s (${fmtQty(perPackage)} u. c/u)`}
                />
              )}
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label={form.inPackages ? `Bueno (${packageName}s)` : 'Bueno'} value={form.good} onChange={e => setForm({ ...form, good: e.target.value })} autoFocus
                  helperText={form.inPackages && form.good ? `${fmtQty(num(form.good) * perPackage)} u.` : ' '} />
                <TextField size="small" type="number" label={form.inPackages ? `Rotos (${packageName}s)` : 'Rotos'} value={form.scrap} onChange={e => setForm({ ...form, scrap: e.target.value })}
                  helperText={form.inPackages && form.scrap ? `${fmtQty(num(form.scrap) * perPackage)} u.` : ' '} />
                <TextField size="small" type="number" label="Horas" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} sx={{ width: 110 }}
                  helperText={standard && form.hours ? `std ${fmtQty(standard * num(form.hours))}` : ' '} />
              </Stack>
              <TextField size="small" label="Operario" value={form.operatorName || ''} onChange={e => setForm({ ...form, operatorName: e.target.value })} />
              <TextField size="small" label="Observaciones" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Ej. paró 2 h por molde, falta de material…" multiline />
              <FormControlLabel
                control={<Checkbox checked={form.applyStock} onChange={e => setForm({ ...form, applyStock: e.target.checked })} />}
                label="Sumar al stock y descontar los componentes (cascos, manijas, mangos)"
              />
              {form.applyStock && (
                <FormControlLabel
                  control={<Checkbox checked={form.consumeMaterials} onChange={e => setForm({ ...form, consumeMaterials: e.target.checked })} />}
                  label="Descontar también la materia prima de la formulación (si no la cargás en Salidas de MP)"
                />
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          {!form?.id && <Button onClick={() => save(true)} disabled={saving}>Guardar y otro</Button>}
          <Button variant="contained" onClick={() => save(false)} disabled={saving}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ==================== Indicadores ====================

const KpisTab: React.FC = () => {
  const [from, setFrom] = useState(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState<ProductionKpis | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    api.getProductionKpis({ from, to }).then(setData).catch(e => setError(errorMessage(e, 'No se pudieron calcular los indicadores')));
  }, [from, to]);

  const maxDay = Math.max(1, ...(data?.days.map(d => d.goodQuantity + d.scrapQuantity) || [1]));

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={2}>
          <KpiStrip>
            <Kpi label="Producido bueno" value={fmtQty(data.goodQuantity)} />
            <Kpi label="Rotos" value={fmtQty(data.scrapQuantity)} hint={`${fmtPct(data.scrapPct, 1)} de lo que salió`} color={data.scrapPct > 0.05 ? '#d32f2f' : undefined} />
            <Kpi label="Eficiencia vs estándar" value={fmtPct(data.efficiencyPct)} hint="Solo turnos con máquina y horas cargadas" />
          </KpiStrip>

          <Card variant="outlined">
            <Typography fontWeight={700} sx={{ p: 2, pb: 0 }}>Por máquina</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Máquina</TableCell>
                    <TableCell align="right">Turnos</TableCell>
                    <TableCell align="right">Horas</TableCell>
                    <TableCell align="right">Bueno</TableCell>
                    <TableCell align="right">Estándar</TableCell>
                    <TableCell align="right">Eficiencia</TableCell>
                    <TableCell align="right">% rotos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.machines.map(m => (
                    <TableRow key={m.machineId ?? 0}>
                      <TableCell>{m.machineName}</TableCell>
                      <TableCell align="right">{m.shifts}</TableCell>
                      <TableCell align="right">{fmtQty(m.hours)}</TableCell>
                      <TableCell align="right">{fmtQty(m.goodQuantity)}</TableCell>
                      <TableCell align="right">{fmtQty(m.expectedQuantity)}</TableCell>
                      <TableCell align="right">{m.efficiencyPct == null ? '—' : <Chip size="small" color={effColor(m.efficiencyPct)} label={fmtPct(m.efficiencyPct)} />}</TableCell>
                      <TableCell align="right">{fmtPct(m.scrapPct, 1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Card variant="outlined" sx={{ flex: 1, width: '100%' }}>
              <Typography fontWeight={700} sx={{ p: 2, pb: 0 }}>Por producto</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Producto</TableCell><TableCell align="right">Bueno</TableCell><TableCell align="right">Rotos</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {data.products.map(p => (
                    <TableRow key={p.productId}>
                      <TableCell>{p.productName}</TableCell>
                      <TableCell align="right">{fmtQty(p.goodQuantity)}</TableCell>
                      <TableCell align="right">{fmtQty(p.scrapQuantity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
            <Card variant="outlined" sx={{ flex: 1, width: '100%', p: 2 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Por día</Typography>
              {data.days.length === 0 && <Typography variant="body2" color="text.secondary">Sin partes en el período.</Typography>}
              {data.days.map(d => (
                <Tooltip key={d.date} title={`${fmtQty(d.goodQuantity)} buenos · ${fmtQty(d.scrapQuantity)} rotos`} placement="left">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ width: 64 }}>{formatDay(d.date)}</Typography>
                    <Box sx={{ flex: 1, display: 'flex', height: 10 }}>
                      <Box sx={{ width: `${(d.goodQuantity / maxDay) * 100}%`, bgcolor: 'primary.main', borderRadius: '3px 0 0 3px' }} />
                      <Box sx={{ width: `${(d.scrapQuantity / maxDay) * 100}%`, bgcolor: 'error.light' }} />
                    </Box>
                  </Stack>
                </Tooltip>
              ))}
            </Card>
          </Stack>
        </Stack>
      )}
    </>
  );
};

// ==================== Máquinas ====================

type MachineForm = Omit<SaveMachine, 'cavitiesTotal' | 'cavitiesUseful' | 'cycleSeconds' | 'standardUnitsPerHour' | 'unitsPerPackage'> & {
  id?: number;
  cavitiesTotal: string;
  cavitiesUseful: string;
  cycleSeconds: string;
  standardUnitsPerHour: string;
  unitsPerPackage: string;
};

const emptyMachine: MachineForm = {
  name: '', code: '', kind: 'Inyectora', status: 'Disponible', initialHours: 0, initialCycles: 0, cavitiesTotal: '', cavitiesUseful: '',
  cycleSeconds: '', standardUnitsPerHour: '', unitsPerPackage: '', packageName: 'bolsa', isActive: true, notes: '',
};

const opt = (v: string) => (v === '' ? null : num(v));

const MachinesTab: React.FC<{ machines: Machine[]; reload: () => Promise<void> }> = ({ machines, reload }) => {
  const [form, setForm] = useState<MachineForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const computed = form && form.cavitiesUseful && num(form.cycleSeconds) > 0
    ? (num(form.cavitiesUseful) * 3600) / num(form.cycleSeconds)
    : null;

  const save = async () => {
    if (!form) return;
    const payload: SaveMachine = {
      name: form.name, code: form.code || null, kind: form.kind || null, status: form.status, initialHours: Number(form.initialHours) || 0,
      initialCycles: Number(form.initialCycles) || 0, isActive: form.isActive, notes: form.notes || null, packageName: form.packageName || null,
      cavitiesTotal: opt(form.cavitiesTotal), cavitiesUseful: opt(form.cavitiesUseful), cycleSeconds: opt(form.cycleSeconds),
      standardUnitsPerHour: opt(form.standardUnitsPerHour), unitsPerPackage: opt(form.unitsPerPackage),
    };
    try {
      if (form.id) await api.updateMachine(form.id, payload);
      else await api.createMachine(payload);
      setForm(null);
      setError(null);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar la máquina'));
    }
  };

  const remove = async (m: Machine) => {
    if (!window.confirm(`¿Borrar ${m.name}? Si tiene partes cargados queda desactivada.`)) return;
    await api.deleteMachine(m.id);
    reload();
  };

  const s = (v?: number | null) => (v == null ? '' : String(v));

  return (
    <>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Estándar por hora = bocas útiles × golpes por hora (3600 / segundos por golpe). Si lo medís distinto, cargalo a mano.
          Las matrices llevan sus bocas y su ciclo: en el parte mandan sobre la máquina.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => setForm({ ...emptyMachine, kind: 'Matriz', packageName: '' })}>Nueva matriz</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ ...emptyMachine })}>Nueva máquina</Button>
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipo</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Bocas (útiles/total)</TableCell>
              <TableCell align="right">Seg. por golpe</TableCell>
              <TableCell align="right">Estándar / hora</TableCell>
              <TableCell align="right">Por bolsa</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {machines.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                <PrecisionManufacturing sx={{ display: 'block', mx: 'auto', mb: 1 }} />Todavía no hay máquinas.
              </TableCell></TableRow>
            )}
            {machines.map(m => (
              <TableRow key={m.id} hover sx={{ opacity: m.isActive ? 1 : 0.5 }}>
                <TableCell>{m.code ? `${m.code} · ` : ''}{m.name}{!m.isActive && <Chip size="small" label="inactiva" sx={{ ml: 1 }} />}</TableCell>
                <TableCell>{m.kind || '—'}</TableCell>
                <TableCell><StatusChip map={EQUIPMENT_STATUS} value={m.status} /></TableCell>
                <TableCell align="right">{m.cavitiesUseful ?? '—'} / {m.cavitiesTotal ?? '—'}</TableCell>
                <TableCell align="right">{m.cycleSeconds ?? '—'}</TableCell>
                <TableCell align="right">{fmtQty(m.effectiveUnitsPerHour)}{m.standardUnitsPerHour != null && <Chip size="small" label="manual" sx={{ ml: 1 }} />}</TableCell>
                <TableCell align="right">{m.unitsPerPackage ? `${fmtQty(m.unitsPerPackage)} u./${m.packageName || 'bolsa'}` : '—'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={() => setForm({
                    id: m.id, name: m.name, code: m.code || '', kind: m.kind || '', status: m.status, initialHours: m.initialHours, initialCycles: m.initialCycles,
                    isActive: m.isActive, notes: m.notes || '', packageName: m.packageName || '',
                    cavitiesTotal: s(m.cavitiesTotal), cavitiesUseful: s(m.cavitiesUseful), cycleSeconds: s(m.cycleSeconds),
                    standardUnitsPerHour: s(m.standardUnitsPerHour), unitsPerPackage: s(m.unitsPerPackage),
                  })}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(m)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.id ? 'Editar equipo' : form?.kind === 'Matriz' ? 'Nueva matriz' : 'Nueva máquina'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Código" value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} sx={{ width: 100 }} />
                <TextField size="small" label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={form.kind === 'Matriz' ? 'Matriz tapón 40 bocas' : 'Iny 5'} required sx={{ flex: 1 }} autoFocus />
                <TextField size="small" select label="Tipo" value={form.kind || ''} onChange={e => setForm({ ...form, kind: e.target.value })} sx={{ width: 150 }}>
                  {['Inyectora', 'Sopladora', 'Molino', 'Armado', 'Matriz', 'Otra'].map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" select label="Estado" value={form.status} onChange={e => setForm({ ...form, status: e.target.value as SaveMachine['status'] })} sx={{ flex: 1 }}>
                  {Object.entries(EQUIPMENT_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="Horas previas" value={form.initialHours} onChange={e => setForm({ ...form, initialHours: num(e.target.value) })} helperText="Horómetro al empezar" />
                <TextField size="small" type="number" label="Ciclos previos" value={form.initialCycles} onChange={e => setForm({ ...form, initialCycles: num(e.target.value) })} helperText="Golpes al empezar" />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Bocas totales" value={form.cavitiesTotal} onChange={e => setForm({ ...form, cavitiesTotal: e.target.value })} />
                <TextField size="small" type="number" label="Bocas útiles" value={form.cavitiesUseful} onChange={e => setForm({ ...form, cavitiesUseful: e.target.value })} />
                <TextField size="small" type="number" label="Seg. por golpe" value={form.cycleSeconds} onChange={e => setForm({ ...form, cycleSeconds: e.target.value })} />
              </Stack>
              <TextField size="small" type="number" label="Estándar por hora (manual)" value={form.standardUnitsPerHour}
                onChange={e => setForm({ ...form, standardUnitsPerHour: e.target.value })}
                helperText={computed ? `Calculado: ${fmtQty(computed)} u./hora · ${fmtQty(computed * 12)} por turno de 12 h` : 'Dejalo vacío para usar bocas × golpes'} />
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Unidades por bolsa" value={form.unitsPerPackage} onChange={e => setForm({ ...form, unitsPerPackage: e.target.value })} helperText="Para cargar el parte en bolsas" />
                <TextField size="small" label="Nombre del bulto" value={form.packageName || ''} onChange={e => setForm({ ...form, packageName: e.target.value })} placeholder="bolsa" />
              </Stack>
              <TextField size="small" label="Notas" value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} multiline />
              <FormControlLabel control={<Checkbox checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activa" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.name}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ==================== Planificar ====================

type PlanRow = { productId: number | null; quantity: string; machineId: number | null; moldId: number | null; date: string };
const emptyPlanRow = (): PlanRow => ({ productId: null, quantity: '', machineId: null, moldId: null, date: todayIso() });

const PlanTab: React.FC<{ machines: Machine[] }> = ({ machines }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const products = useProducts();
  const initial = (location.state as { lines?: PlanLine[] } | null)?.lines;
  const [rows, setRows] = useState<PlanRow[]>(() => initial?.length
    ? initial.map(l => ({ productId: l.productId, quantity: String(l.quantity), machineId: null, moldId: null, date: todayIso() }))
    : [emptyPlanRow()]);
  const [result, setResult] = useState<PlanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const presses = machines.filter(m => !isMold(m));
  const molds = machines.filter(isMold);

  const calculate = useCallback(async (list: PlanRow[]) => {
    const lines = list.filter(r => r.productId && num(r.quantity) > 0)
      .map(r => ({ productId: r.productId!, quantity: num(r.quantity), machineId: r.machineId, moldId: r.moldId, date: r.date || null }));
    if (lines.length === 0) { setError('Cargá al menos un producto con cantidad'); return; }
    try {
      setResult(await api.planProduction(lines));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'No se pudo calcular'));
    }
  }, []);

  useEffect(() => { if (initial?.length) calculate(rows); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i: number, patch: Partial<PlanRow>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const missing = result?.requirements.filter(r => r.kind === 'material' && r.shortage > 0) ?? [];

  const requestPurchase = async () => {
    try {
      await api.createPurchaseRequest({
        priority: 'Alta',
        notes: `Faltantes de la planificación de producción (${result!.lines.map(l => `${fmtQty(l.quantity)} ${l.productName}`).join(', ')})`,
        items: missing.map(m => ({ materialId: m.id, quantity: Math.ceil(m.shortage * 100) / 100, notes: `Necesario ${fmtQty(m.required)} ${m.unit || ''}, hay ${fmtQty(m.available)}` })),
      });
      setMessage('Pedido interno de compra creado con los faltantes.');
    } catch (e) {
      setError(errorMessage(e, 'No se pudo crear el pedido de compra'));
    }
  };

  const addComponent = (productId: number, shortage: number) => {
    const next = [...rows, { ...emptyPlanRow(), productId, quantity: String(Math.ceil(shortage)) }];
    setRows(next);
    calculate(next);
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Qué hace falta para fabricar: insumos necesarios contra lo disponible, faltantes a comprar, horas de máquina y paradas de mantenimiento previstas.
        Confirmar lo fabricado se hace en el parte diario.
      </Typography>
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          {rows.map((r, i) => (
            <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 2, minWidth: 220 }}><ProductSelect value={r.productId} onChange={id => set(i, { productId: id })} /></Box>
              <TextField size="small" type="number" label="Cantidad" value={r.quantity} onChange={e => set(i, { quantity: e.target.value })} sx={{ width: 130 }} />
              <TextField size="small" select label="Máquina" value={r.machineId ?? ''} onChange={e => set(i, { machineId: e.target.value === '' ? null : Number(e.target.value) })} sx={{ minWidth: 150 }}>
                <MenuItem value="">—</MenuItem>
                {presses.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
              {molds.length > 0 && (
                <TextField size="small" select label="Matriz" value={r.moldId ?? ''} onChange={e => set(i, { moldId: e.target.value === '' ? null : Number(e.target.value) })} sx={{ minWidth: 150 }}>
                  <MenuItem value="">—</MenuItem>
                  {molds.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </TextField>
              )}
              <TextField size="small" type="date" label="Para" value={r.date} onChange={e => set(i, { date: e.target.value })} InputLabelProps={{ shrink: true }} />
              <IconButton size="small" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<Add />} onClick={() => setRows([...rows, emptyPlanRow()])}>Agregar producto</Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={() => calculate(rows)}>Calcular</Button>
          </Stack>
        </Stack>
      </Card>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}
        action={<Button color="inherit" size="small" onClick={() => navigate('/purchase-requests')}>Ver pedidos</Button>}>{message}</Alert>}
      {result && (
        <Stack spacing={2}>
          {result.lines.some(l => l.warnings.length > 0 || l.hoursNeeded) && (
            <Card variant="outlined" sx={{ p: 2 }}>
              {result.lines.map((l, i) => (
                <Box key={i} sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    <b>{fmtQty(l.quantity)} {l.productName}</b>
                    {l.hoursNeeded ? ` · ${fmtQty(l.hoursNeeded)} h de ${l.moldName || l.machineName} (${fmtQty(l.hoursNeeded / 12)} turnos de 12 h)` : ''}
                  </Typography>
                  {l.warnings.map((w, j) => <Typography key={j} variant="caption" color="warning.main" display="block">⚠ {w}</Typography>)}
                </Box>
              ))}
            </Card>
          )}
          <Card variant="outlined">
            <Stack direction="row" alignItems="center" sx={{ p: 2, pb: 0 }}>
              <Typography fontWeight={700} sx={{ flex: 1 }}>Insumos necesarios</Typography>
              {missing.length > 0 && <Button size="small" variant="contained" onClick={requestPurchase}>Pedir lo que falta ({missing.length})</Button>}
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Insumo</TableCell>
                  <TableCell align="right">Necesario</TableCell>
                  <TableCell align="right">Disponible</TableCell>
                  <TableCell align="right">Falta</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {result.requirements.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Los productos no tienen formulación cargada.</TableCell></TableRow>}
                {result.requirements.map(r => (
                  <TableRow key={`${r.kind}${r.id}`}>
                    <TableCell>{r.name}{r.kind === 'product' && <Chip size="small" label="se fabrica" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell align="right">{fmtQty(r.required)} {r.unit || ''}</TableCell>
                    <TableCell align="right">{fmtQty(r.available)}</TableCell>
                    <TableCell align="right">{r.shortage > 0 ? <Chip size="small" color="error" label={fmtQty(r.shortage)} /> : <Chip size="small" color="success" label="alcanza" />}</TableCell>
                    <TableCell align="right">
                      {r.kind === 'product' && r.shortage > 0 && products.some(p => p.id === r.id) && (
                        <Button size="small" onClick={() => addComponent(r.id, r.shortage)}>Planificar también</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card variant="outlined">
            <Typography fontWeight={700} sx={{ p: 2, pb: 0 }}>Paradas de mantenimiento hasta el {formatDay(result.to)}</Typography>
            <Table size="small">
              <TableBody>
                {result.stops.length === 0 && <TableRow><TableCell sx={{ color: 'text.secondary' }}>No hay paradas previstas para estos equipos.</TableCell></TableRow>}
                {result.stops.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{formatDay(s.date)}</TableCell>
                    <TableCell>{s.machineName}</TableCell>
                    <TableCell>{s.title}</TableCell>
                    <TableCell align="right">{s.estimatedDowntimeHours ? `${fmtQty(s.estimatedDowntimeHours)} h de parada` : ''}</TableCell>
                    <TableCell align="right"><StatusChip map={DUE_CHIP} value={s.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Stack>
      )}
    </>
  );
};

// ==================== Salidas de MP ====================

type IssueForm = { date: string; productId: number | null; machineId: number | null; document: string; notes: string; lines: { materialId: number | null; quantity: string }[] };

const IssuesTab: React.FC<{ machines: Machine[] }> = ({ machines }) => {
  const [from, setFrom] = useState(addDaysIso(todayIso(), -6));
  const [to, setTo] = useState(todayIso());
  const [rows, setRows] = useState<MaterialIssue[]>([]);
  const [form, setForm] = useState<IssueForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(false);
  const materials = useMaterials(refresh);

  const load = useCallback(async () => {
    try {
      setRows(await api.getMaterialIssues({ from, to }));
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron cargar las salidas'));
    }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    const lines = form.lines.filter(l => l.materialId && num(l.quantity) > 0).map(l => ({ materialId: l.materialId!, quantity: num(l.quantity) }));
    try {
      await api.createMaterialIssues({ date: form.date, productId: form.productId, machineId: form.machineId, document: form.document || undefined, notes: form.notes || undefined, lines });
      setForm(null);
      setError(null);
      await loadMaterials(true);
      setRefresh(r => !r);
      load();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };

  const remove = async (r: MaterialIssue) => {
    if (!window.confirm(`¿Borrar la salida de ${fmtQty(r.quantity)} kg de ${r.materialName}? Vuelve al stock.`)) return;
    await api.deleteMaterialIssue(r.id);
    await loadMaterials(true);
    setRefresh(x => !x);
    load();
  };

  const total = rows.reduce((a, r) => a + r.quantity, 0);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Materia prima que sale del depósito a cada inyectora: descuenta el stock de MP.</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setForm({ date: todayIso(), productId: null, machineId: null, document: '', notes: '', lines: [{ materialId: null, quantity: '' }] })}>
          Cargar salida
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Materia prima</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Inyectora</TableCell>
                <TableCell align="right">Kg</TableCell>
                <TableCell>Comprobante</TableCell>
                <TableCell>Observaciones</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin salidas en estas fechas.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{formatDay(r.date)}</TableCell>
                  <TableCell>{r.materialName}</TableCell>
                  <TableCell>{r.productName || '—'}</TableCell>
                  <TableCell>{r.machineName || '—'}</TableCell>
                  <TableCell align="right">{fmtQty(r.quantity)}</TableCell>
                  <TableCell>{r.document || ''}</TableCell>
                  <TableCell>{r.notes || ''}</TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => remove(r)}><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && <TableRow><TableCell colSpan={4}><b>Total</b></TableCell><TableCell align="right"><b>{fmtQty(total)}</b></TableCell><TableCell colSpan={3} /></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Salida de materia prima</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="date" label="Fecha" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} InputLabelProps={{ shrink: true }} />
                <TextField size="small" select label="Inyectora destino" value={form.machineId ?? ''} onChange={e => setForm({ ...form, machineId: e.target.value === '' ? null : Number(e.target.value) })} sx={{ flex: 1 }}>
                  <MenuItem value="">—</MenuItem>
                  {machines.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </TextField>
              </Stack>
              <ProductSelect value={form.productId} onChange={id => setForm({ ...form, productId: id })} label="Para qué producto" />
              {form.lines.map((l, i) => (
                <Stack key={i} direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <MaterialSelect options={materials} value={l.materialId} onChange={id => setForm({ ...form, lines: form.lines.map((x, j) => (j === i ? { ...x, materialId: id } : x)) })} />
                  </Box>
                  <TextField size="small" type="number" label="Kg" value={l.quantity} sx={{ width: 110 }}
                    onChange={e => setForm({ ...form, lines: form.lines.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)) })} />
                  <IconButton size="small" disabled={form.lines.length === 1} onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}><Delete fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start' }} onClick={() => setForm({ ...form, lines: [...form.lines, { materialId: null, quantity: '' }] })}>Otra materia prima</Button>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Remito o comprobante" value={form.document} onChange={e => setForm({ ...form, document: e.target.value })} />
                <TextField size="small" label="Observaciones" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} sx={{ flex: 1 }} placeholder="Ej. muestra" />
              </Stack>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FactoryProductionPage;
